import { personDisplayName } from "@/lib/people";
import { memberActions } from "@/lib/project-access";
import type {
  IssueUpdate,
  ProjectDetail,
  ProjectIssue,
  ProjectUser,
} from "@/types/api";
import type { Action } from "@/types/permissions";

/**
 * Display identity for an issue: zero-padded to three digits (`001`).
 *
 * Padded rather than bare because these get quoted out loud and written on
 * paper — "issue oh-oh-one" reads as an identifier, "issue 1" reads as a count.
 * Numbers past 999 render in full rather than truncating.
 */
export function formatIssueNumber(issueNumber: number): string {
  return String(issueNumber).padStart(3, "0");
}

/**
 * Full name, falling back to email, then to a stable placeholder.
 *
 * `null` is a real value here, not a loading state: `assigned_to` is null when
 * nobody owns the issue, and `created_by` / `author` are null when the account
 * has been removed. The caller decides which wording fits — this returns
 * "Unknown user" so a trail entry never renders a nameless line.
 *
 * The name/email resolution itself lives in `personDisplayName` — shared with
 * every other surface that renders a person — and this only supplies the
 * issue-side wording for the no-identity case.
 */
export function personName(person: ProjectUser | null | undefined): string {
  return personDisplayName(person) ?? "Unknown user";
}

/** Up to two initials for an avatar fallback. */
export function personInitials(person: ProjectUser | null | undefined): string {
  if (!person) return "?";
  const first = person.first_name.trim();
  const last = person.last_name.trim();
  const initials = `${first.charAt(0)}${last.charAt(0)}`.trim();
  return (initials || person.email.charAt(0) || "?").toUpperCase();
}

/**
 * Whole days an issue is past its due date, or `null` when it is not overdue.
 *
 * A closed issue is never overdue — finished late is finished, and flagging it
 * in red forever would bury the ones that still need chasing.
 *
 * Compared in **local calendar days**, not elapsed hours: an issue due today is
 * not late at 23:00, and one due yesterday is "1 day over" at 00:05 rather than
 * at the 24-hour mark. `due_date` is a date with no time, so treating it as an
 * instant would drift by the viewer's UTC offset.
 */
export function overdueDays(
  issue: Pick<ProjectIssue, "due_date" | "status">,
  now: Date = new Date(),
): number | null {
  if (!issue.due_date) return null;
  if (issue.status === "closed") return null;

  const parts = issue.due_date.split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [year, month, day] = parts;

  const due = Date.UTC(year, month - 1, day);
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = Math.round((today - due) / 86_400_000);

  return days > 0 ? days : null;
}

/** `"3 days over"` / `"1 day over"`, or `null` when the issue is not late. */
export function overdueLabel(
  issue: Pick<ProjectIssue, "due_date" | "status">,
  now: Date = new Date(),
): string | null {
  const days = overdueDays(issue, now);
  if (days == null) return null;
  return `${days} ${days === 1 ? "day" : "days"} over`;
}

/**
 * Whether `user` may close `issue`.
 *
 * Mirrors the backend's rule exactly: the raiser may close by identity,
 * whatever verbs their role holds; everyone else needs `issue:Close`; the
 * project owner is covered because `can()` already short-circuits for them.
 *
 * This is for hiding affordances only. The server decides. See [[issues.spec]].
 */
export function canCloseIssue(
  issue: Pick<ProjectIssue, "created_by">,
  currentUserId: string | undefined,
  can: (action: "issue:Close") => boolean,
): boolean {
  if (currentUserId && issue.created_by?.id === currentUserId) return true;
  return can("issue:Close");
}

/**
 * The parts of an issue the permission helpers read. `assigned_to` and
 * `viewer_involved` are optional so a caller holding less of the issue can
 * still ask; without them, only the raiser counts as involved.
 */
export type IssueAccessFields = Pick<ProjectIssue, "status" | "created_by"> &
  Partial<Pick<ProjectIssue, "assigned_to" | "viewer_involved">>;

/** The issue verbs that decide who may post on an issue. */
type IssuePostVerb = "issue:Update:All" | "issue:Update:Involved" | "issue:Update";

/**
 * Whether the viewer is involved in `issue`: raised it, is assigned to it, or
 * was invited to it.
 *
 * The server says so in `viewer_involved`, because only it knows who was
 * invited. A backend that predates invitations leaves that out, and raiser or
 * assignee is then the whole rule. See [[permissions.spec]] § All vs Involved
 * scoping.
 */
export function isInvolvedInIssue(
  issue: Pick<IssueAccessFields, "created_by" | "assigned_to" | "viewer_involved">,
  currentUserId: string | undefined,
): boolean {
  if (issue.viewer_involved !== undefined) return issue.viewer_involved;
  if (!currentUserId) return false;
  return (
    issue.created_by?.id === currentUserId ||
    issue.assigned_to?.id === currentUserId
  );
}

/**
 * Whether the viewer may edit an issue's header: title, description, assignee,
 * due date. `issue:Edit`, or the legacy `issue:Update` it was split from.
 *
 * Status is the caller's business: a closed issue's header is frozen whatever
 * this says.
 */
export function canEditIssue(
  can: (action: "issue:Edit" | "issue:Update") => boolean,
): boolean {
  return can("issue:Edit") || can("issue:Update");
}

/**
 * Whether the viewer may reopen a closed issue, which is posting `in_progress`
 * on it. Only posting on any issue (`issue:Update:All`, or the legacy
 * `issue:Update`) reaches a closed one; posting on issues you are involved in
 * stops at close.
 */
export function canReopenIssue(
  can: (action: "issue:Update:All" | "issue:Update") => boolean,
): boolean {
  return can("issue:Update:All") || can("issue:Update");
}

/**
 * Whether the viewer may post `in_progress` or `completed` on `issue`.
 *
 * Mirrors the backend: posting on any issue covers every issue, closed ones
 * included; `issue:Update:Involved` covers only open issues the viewer is
 * involved in. Closing is a separate question, {@link canCloseIssue}.
 *
 * For hiding affordances only. The server decides.
 */
export function canPostIssueUpdate(
  issue: IssueAccessFields,
  currentUserId: string | undefined,
  can: (action: IssuePostVerb) => boolean,
): boolean {
  if (canReopenIssue(can)) return true;
  return (
    issue.status !== "closed" &&
    can("issue:Update:Involved") &&
    isInvolvedInIssue(issue, currentUserId)
  );
}

/**
 * The issue permissions the backend's rewrite introduced. Holding none of them
 * means the old model, where roles carried no issue View permission and every
 * member saw every issue.
 */
const REWRITTEN_ISSUE_ACTIONS = [
  "issue:View:All",
  "issue:View:Involved",
  "issue:Edit",
  "issue:Update:All",
  "issue:Update:Involved",
] as const satisfies readonly Action[];

type IssueViewVerb = (typeof REWRITTEN_ISSUE_ACTIONS)[number];

/**
 * Legacy: true until the backend's issue-permission rewrite is in production,
 * for anyone whose roles predate it. Remove with the other `issue:Update`
 * fallbacks once it is. A role made after the rewrite with no issue
 * permissions also reads as old; the server then simply sends no issues.
 */
function predatesIssueRewrite(can: (action: IssueViewVerb) => boolean): boolean {
  return !REWRITTEN_ISSUE_ACTIONS.some((action) => can(action));
}

/**
 * Whether the viewer sees issues at all: either issue View permission. Puts
 * the Issues tab in the project's navigation. See [[bottom-nav.spec]] § Tabs
 * follow permissions.
 */
export function canViewIssues(can: (action: IssueViewVerb) => boolean): boolean {
  return (
    can("issue:View:All") ||
    can("issue:View:Involved") ||
    predatesIssueRewrite(can)
  );
}

/**
 * Whether the viewer sees every issue, rather than only the ones they're on.
 * Gates whatever lists the project's members on the Issues page — the Assigned
 * to and Raised by filters. See [[issues-page.spec]] § Only the issues you're
 * on.
 */
export function canViewAllIssues(
  can: (action: IssueViewVerb) => boolean,
): boolean {
  return can("issue:View:All") || predatesIssueRewrite(can);
}

/** The one status action an issue offers: post an update, or reopen it. */
export type IssueStatusAction = "update" | "reopen";

/**
 * Which status action `issue` offers this viewer, or `null` for none.
 *
 * The issue page's bottom bar and the Issues list's swipe and status pill all
 * ask this, so the list never offers what the page withholds. Open: Update, to
 * anyone who may post any status at all — the sheet decides which. Closed:
 * Reopen, gated on posting to any issue rather than on closing, because the
 * status it posts is `in_progress` and that is the backend's rule for it. See
 * [[issue-detail-page.spec]] and [[issue-item.spec]].
 *
 * For hiding affordances only. The server decides.
 */
export function issueStatusAction(
  issue: IssueAccessFields,
  currentUserId: string | undefined,
  can: (action: IssuePostVerb | "issue:Close") => boolean,
): IssueStatusAction | null {
  if (issue.status === "closed") return canReopenIssue(can) ? "reopen" : null;
  return canPostIssueUpdate(issue, currentUserId, can) ||
    canCloseIssue(issue, currentUserId, can)
    ? "update"
    : null;
}

/** Format an ISO date (no time) as `15 Aug 2026`. */
/**
 * Whether an issue's due and target dates are shown — on the detail heading and
 * on a list card. Done work (completed or closed) has nothing left to chase, so
 * both are hidden. The desktop table keeps them as columns in every status. See
 * [[issue-date-pill.spec]].
 */
export function showsIssueDates(issue: Pick<ProjectIssue, "status">): boolean {
  return issue.status !== "completed" && issue.status !== "closed";
}

export function formatDueDate(isoDate: string | null): string | null {
  const parts = formatDueDateParts(isoDate);
  return parts ? `${parts.dayMonth} ${parts.year}` : null;
}

/**
 * The same date split into `15 Aug` and `2026`, for hosts that drop the year
 * where space is short (the issue card on a phone). Joined with a space, the
 * two are exactly {@link formatDueDate}.
 */
export function formatDueDateParts(
  isoDate: string | null,
): { dayMonth: string; year: string } | null {
  if (!isoDate) return null;
  const parts = isoDate.split("-").map(Number);
  if (parts.length !== 3 || parts.some(Number.isNaN)) return null;
  const [year, month, day] = parts;
  // Anchored to UTC: a date-only value read as an instant would drift a day
  // for viewers behind it.
  const dayMonth = new Date(Date.UTC(year, month - 1, day)).toLocaleDateString(
    "en-GB",
    { day: "numeric", month: "short", timeZone: "UTC" },
  );
  return { dayMonth, year: String(year) };
}

/** Milliseconds for a comparison, or `null` when the value cannot be read. */
function timestampMs(value: string | null): number | null {
  const ms = value ? new Date(value).getTime() : Number.NaN;
  return Number.isNaN(ms) ? null : ms;
}

/**
 * The issue's current target date, as the ISO date an update carried.
 *
 * Derived on read rather than stored, because the issue has no target of its
 * own: a target belongs to the update that forecast it, beside the status it
 * was forecast with ([[issues.spec]]). A `target_date` on the issue would be a
 * cache of the trail, and a cache that can disagree with an append-only log is
 * worth less than no cache at all. The detail read already carries every
 * update, so deriving it costs no extra request.
 *
 * The newest **non-null** one wins, not the newest update: a later update
 * carrying no target says nothing about the target rather than clearing it. A
 * forecast is superseded, never retracted — which is why nothing here treats a
 * null as a value.
 *
 * Compared on each update's own `created_at`, falling back to the order the
 * list arrived in when two share a millisecond: the compare is strict, so the
 * first of a tied pair keeps the target. Ties are reachable rather than
 * theoretical — the server serialises microseconds and `Date.getTime()`
 * truncates to milliseconds — and the fallback is left as it is rather than
 * broken by a tiebreak, because the list the API returns is already
 * chronological, and `newestFirst` in the trail lands on that same arrival
 * order on a tie (its comparator returns 0, and the sort is stable). Header
 * and trail therefore always name the same update as the current forecast.
 *
 * An update whose timestamp cannot be read never displaces one whose can,
 * matching the foot-of-the-trail treatment such an entry already gets.
 *
 * Returns the raw ISO date; the caller renders it through
 * {@link formatDueDate}, so a target reads exactly as the due date beside it.
 */
export function currentTargetDate(
  updates: Pick<IssueUpdate, "created_at" | "target_date">[] | undefined,
): string | null {
  let newest: { at: number | null; target: string } | null = null;

  for (const update of updates ?? []) {
    if (!update.target_date) continue;

    const at = timestampMs(update.created_at);
    if (!newest) {
      newest = { at, target: update.target_date };
      continue;
    }
    // An unreadable timestamp is never the newest thing on the issue; it only
    // stands when nothing readable forecast a date at all.
    if (at === null) continue;
    if (newest.at === null || at > newest.at) {
      newest = { at, target: update.target_date };
    }
  }

  return newest?.target ?? null;
}

/**
 * Title given to an issue nobody named — the last-ditch stand-in that keeps a
 * blank title from ever reaching the server.
 *
 * Deliberately plain: it is a marker that this one still needs naming, not an
 * attempt to guess what the problem was.
 */
export const PLACEHOLDER_ISSUE_TITLE = "Issue";

/**
 * The first non-blank candidate, or {@link PLACEHOLDER_ISSUE_TITLE}.
 *
 * `title` is required server-side ([[issues.spec]]), and the capture-time
 * toggle deliberately does NOT block a submit on an empty one — someone
 * rushing a snagging walk must never be stopped by a form. That trade only
 * holds if a blank title cannot cost them anything, hence this: the queued
 * create carries the issue block on the record's own upload, so a title the
 * server refuses would fail the whole record permanently (a non-retryable 400
 * — see `isRetryable`) and lose the photo, not just the issue.
 *
 * Candidates are tried in order — what the user typed, then whatever the
 * caller considers a reasonable stand-in (the record's remarks, at capture
 * time) — so the placeholder is genuinely the last resort.
 */
export function issueTitleOrPlaceholder(
  ...candidates: (string | null | undefined)[]
): string {
  for (const candidate of candidates) {
    const trimmed = candidate?.trim();
    if (trimmed) return trimmed;
  }
  return PLACEHOLDER_ISSUE_TITLE;
}

/**
 * The line a shared invite link travels under: `Issue 024 · Fire-stopping
 * missing at riser, L3`. The sender can edit it before sending; nothing else
 * from the issue goes with the link. See [[add-contributor-sheet.spec]].
 */
export function issueShareText(issue: Pick<ProjectIssue, "number" | "title">): string {
  return `Issue ${formatIssueNumber(issue.number)} · ${issue.title}`;
}

/**
 * Whether someone sees every issue on the project, whatever they're on: the
 * owner, or roles holding `issue:View:All`. `null` when it can't be told.
 * Picks the wording of the remove confirmation — taking them off an issue
 * doesn't hide it from them ([[issue-contributors.spec]]).
 */
export function seesEveryIssue(
  project: Pick<ProjectDetail, "owner" | "members" | "roles">,
  userId: string,
): boolean | null {
  if (project.owner.id === userId) return true;
  const actions = memberActions(project, userId);
  return actions ? actions.includes("issue:View:All") : null;
}
